import React, { useState, useEffect, useMemo } from 'react';
import { dataService } from '../../services/dataService';
import {
  agentStats, functionStats, capProgress, formatUsd, tierPricing, estimateTypicalTask,
} from '../../utils/agentCost';
import { getWorkspaceAiKeyStatus } from '../../services/aiKey';
import { useChatDock } from '../../contexts/ChatDockContext';

/**
 * Every team and agent in one place: what each is doing and what it costs.
 *
 * Three cost figures rather than one, because a single total cannot answer the
 * three questions people actually have — is this agent expensive right now, has
 * it been expensive today, and has it been expensive ever. The first is the one
 * that lets someone stop a runaway while it is still running.
 */
export default function AgentDashboard({ workspaceId, nodes, opsMode }) {
  const [runs, setRuns]   = useState([]);
  const [chats, setChats] = useState([]);
  const { openChat } = useChatDock();
  const [now, setNow]     = useState(Date.now());
  const [keyStatus, setKeyStatus] = useState(null);

  useEffect(() => {
    if (!workspaceId) { setKeyStatus(null); return; }
    let alive = true;
    getWorkspaceAiKeyStatus(workspaceId).then(k => { if (alive) setKeyStatus(k); });
    return () => { alive = false; };
  }, [workspaceId]);

  useEffect(() => {
    if (!workspaceId) { setRuns([]); return; }
    const unsubRuns  = dataService.subscribeAgentRuns(workspaceId, setRuns);
    const unsubChats = dataService.subscribeAgentChats(workspaceId, setChats);
    return () => { unsubRuns(); unsubChats(); };
  }, [workspaceId]);

  // The "last 24 hours" window and any live spend both move on their own, so
  // the view re-reads the clock rather than freezing at first render.
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 20000);
    return () => clearInterval(t);
  }, []);

  const functions = useMemo(
    () => (nodes || []).filter(n => n.type === 'functionNode'),
    [nodes],
  );

  const anyRuns = runs.length > 0 || chats.length > 0;

  if (!workspaceId) return <div className="agents-page-empty"><p>Select a workspace.</p></div>;

  if (functions.length === 0) {
    return (
      <div className="agents-page-empty">
        <div className="agents-empty-icon">🤖</div>
        <p>No teams yet. Add a Function node to your pipeline — that is the team an agent belongs to.</p>
      </div>
    );
  }

  return (
    <div className="agent-dash">
      {keyStatus && !keyStatus.configured && (
        <div className="agent-dash-note agent-dash-warn">
          <strong>No AI key for this workspace.</strong> Agents run on your own Anthropic
          account, so nothing here can work until a key is added under <strong>Connectors →
          Claude (Anthropic)</strong>. You can still set teams and agents up in the meantime.
        </div>
      )}
      {keyStatus?.status === 'invalid' && (
        <div className="agent-dash-note agent-dash-warn">
          <strong>Anthropic rejected this workspace's key.</strong> Agents will not run until
          it is replaced under Connectors.{keyStatus.lastFailure ? ` (${keyStatus.lastFailure})` : ''}
        </div>
      )}

      {!anyRuns && (
        <div className="agent-dash-note">
          No agent has run yet, so the cost columns are empty. They fill in as soon as a pipeline
          step gives one of these agents a task. The figures beside each agent are estimates of
          what one task would cost at its tier.
        </div>
      )}

      {functions.map(fn => {
        const agents = fn.data?.agents || [];
        const fstats = functionStats(runs, agents, now, chats);
        return (
          <div key={fn.id} className="agent-dash-team">
            <div className="agent-dash-team-head">
              <div>
                <span className="agent-dash-team-name">{fn.data?.label || fn.id}</span>
                <span className="agent-dash-team-sub">
                  {agents.length} {opsMode ? 'employee' : 'agent'}{agents.length === 1 ? '' : 's'}
                  {fstats.working > 0 && <span className="agent-dash-live"> · {fstats.working} working now</span>}
                </span>
              </div>
              <div className="agent-dash-team-costs">
                <span title="Spend on tasks in hand right now">
                  now <strong>{formatUsd(fstats.currentUsd)}</strong>
                </span>
                <span title="Spend in the last 24 hours">
                  24h <strong>{formatUsd(fstats.last24Usd)}</strong>
                </span>
                <span title="Everything this team has ever spent">
                  total <strong>{formatUsd(fstats.lifetimeUsd)}</strong>
                </span>
              </div>
            </div>

            {agents.length === 0 ? (
              <div className="agents-fn-empty">No agents on this team yet.</div>
            ) : (
              <table className="agent-dash-table">
                <thead>
                  <tr>
                    <th>Agent</th>
                    <th>Status</th>
                    <th className="num" title="Spend on the task it is working on right now">This task</th>
                    <th className="num" title="Spend in the last 24 hours">24 hours</th>
                    <th className="num" title="Everything this agent has ever spent">Lifetime</th>
                    <th className="num">Runs</th>
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {agents.map(a => {
                    const s = agentStats(runs, a.id, now, chats);
                    const pct = capProgress(s);
                    return (
                      <tr key={a.id} className={s.status === 'working' ? 'is-working' : ''}>
                        <td>
                          <div className="agent-dash-name">
                            {a.name || 'Agent'}
                            {a.isManager && <span className="agent-manager-tag">{opsMode ? 'Head' : 'Manager'}</span>}
                          </div>
                          <div className="agent-dash-role">
                            {tierPricing(a.model).label}
                            {a.role ? ` · ${a.role}` : ''}
                          </div>
                        </td>
                        <td>
                          {s.status === 'working' ? (
                            <div className="agent-dash-working">
                              <span className="agent-dash-dot" />
                              Working
                              {s.currentTask && (
                                <div className="agent-dash-task" title={s.currentTask}>
                                  {String(s.currentTask).replace(/\s+/g, ' ').slice(0, 48)}…
                                </div>
                              )}
                              {pct !== undefined && (
                                <div className="agent-dash-cap" title={`${formatUsd(s.currentUsd)} of its ${formatUsd(s.currentCapUsd)} cap`}>
                                  <div className="agent-dash-cap-fill" style={{ width: `${Math.round(pct * 100)}%` }} />
                                </div>
                              )}
                            </div>
                          ) : s.status === 'stalled' ? (
                            <div className="agent-dash-stalled" title="Started a long time ago and never finished. Its work may already be done — check Logs.">
                              ⚠ Stuck
                              {s.currentTask && (
                                <div className="agent-dash-task">{String(s.currentTask).replace(/\s+/g, ' ').slice(0, 48)}…</div>
                              )}
                            </div>
                          ) : a.paused ? (
                            <span className="agent-dash-idle">Paused</span>
                          ) : (
                            <span className="agent-dash-idle">Idle</span>
                          )}
                        </td>
                        <td className="num">
                          {s.status === 'working' || s.status === 'stalled'
                            ? formatUsd(s.currentUsd)
                            : <span className="agent-dash-dash">—</span>}
                        </td>
                        <td className="num">{s.runs ? formatUsd(s.last24Usd) : <span className="agent-dash-dash">—</span>}</td>
                        <td className="num">
                          {(s.runs || s.chatUsd > 0)
                            ? formatUsd(s.lifetimeUsd)
                            : <span className="agent-dash-est" title="No runs yet — this is what one task would cost at this tier">
                                ≈{formatUsd(estimateTypicalTask(a.model))}
                              </span>}
                        </td>
                        <td className="num">
                          {s.runs || <span className="agent-dash-dash">—</span>}
                          {(s.capped > 0 || s.failed > 0) && (
                            <div className="agent-dash-trouble" title={`${s.capped} hit their cap, ${s.failed} failed`}>
                              {s.capped > 0 && `${s.capped} capped`}
                              {s.capped > 0 && s.failed > 0 && ', '}
                              {s.failed > 0 && `${s.failed} failed`}
                            </div>
                          )}
                        </td>
                        <td>
                          <button
                            className="knowledge-btn"
                            onClick={() => openChat({ id: `${fn.id}__${a.id}`, agent: a, fnNode: fn, workspaceId })}
                          >
                            Ask
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        );
      })}

    </div>
  );
}
